import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { formatSoles, fileUrl } from "@/api";
import { useStore } from "@/context/StoreContext";
import { ShoppingBag, Package2, Clock3 } from "lucide-react";

export default function ProductCard({ product, index = 0 }) {
  const { addToCart } = useStore();
  const inStock = product.stock_status === "in_stock";
  const img = fileUrl(product.images?.[0]);

  const wholesale = (product.wholesale_tiers || [])[0];
  const hasPromo = product.promo_price && Number(product.promo_price) < Number(product.retail_price);
  const navigate = useNavigate();

  return (
    <div
      className="group bg-white rounded-2xl overflow-hidden card-hover border border-black/5 anim-fade-up flex flex-col"
      style={{ animationDelay: `${index * 50}ms` }}
      data-testid={`product-card-${product.code}`}
    >
      <Link to={`/producto/${product.id}`} className="relative block aspect-[4/5] overflow-hidden bg-neutral-100">
        {img ? (
          <img
            src={img}
            alt={product.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-neutral-300">
            <Package2 size={60} />
          </div>
        )}
        <div className="absolute top-3 left-3 flex flex-col gap-2">
          <span
            className={`px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider ${
              inStock ? "badge-stock" : "badge-preorder"
            }`}
            data-testid={`stock-badge-${product.code}`}
          >
            {inStock ? "En stock" : "Bajo pedido"}
          </span>
          {!inStock && product.production_time && (
            <span className="px-3 py-1 rounded-full text-[11px] font-semibold bg-white/90 flex items-center gap-1">
              <Clock3 size={12} /> {product.production_time}
            </span>
          )}
        </div>
      </Link>
      <div className="p-5 flex-1 flex flex-col">
        <div className="text-[11px] uppercase tracking-widest text-neutral-400 font-semibold">
          {product.code}
        </div>
        <Link to={`/producto/${product.id}`} className="mt-1 font-serif text-xl font-semibold leading-tight hover:text-[var(--brand-primary)]">
          {product.name}
        </Link>
        <div className="mt-auto pt-4 flex items-end justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-400">{hasPromo ? "Promoción" : "Minorista"}</div>
            <div className="text-2xl font-bold text-[var(--brand-primary)]" data-testid={`price-${product.code}`}>
              {formatSoles(hasPromo ? product.promo_price : product.retail_price)}
              {hasPromo && <span className="ml-2 text-sm font-medium text-neutral-400 line-through">{formatSoles(product.retail_price)}</span>}
            </div>
            {wholesale && (
              <div className="text-xs text-neutral-500 mt-1">
                Mayorista desde {wholesale.min_qty} u. · {formatSoles(wholesale.price)}
              </div>
            )}
          </div>
          <Button
            onClick={() => (product.variants?.length ? navigate(`/producto/${product.id}`) : addToCart(product, product.min_order_qty || 1))}
            className="btn-primary rounded-full h-10 px-4"
            data-testid={`add-to-cart-${product.code}`}
          >
            <ShoppingBag size={16} />
          </Button>
        </div>
      </div>
    </div>
  );
}
